import { Cartesian2, Cartesian3, createGuid } from "cesium";
import { EarthBrainUtils } from "../utilities/earthbrainutility";
import { IPLine } from "./routes/ip-line";
import { MainLine } from "./routes/main-line";
import { SIMARouteType } from "@earthbrain/sima";
import { CenterLine } from "./routes/center-line";
import { PointType, } from "../site/arbitrary-line/types";
/** Segments shorter than this (m, horizontal) are treated as zero length */
const MIN_SEGMENT_LENGTH = 1e-6;
/**
 * It will calculate all the geometric points for the routes
 */
export class SIMA {
    _id;
    _simaDoc;
    _surveyPoints;
    _routeByGroup;
    _centerlineDirections;
    constructor(simaDoc, id) {
        this._id = id ?? createGuid();
        this._simaDoc = simaDoc;
        this._surveyPoints = new Map();
        this._routeByGroup = new Map();
    }
    get id() {
        return this._id;
    }
    /**
     * Gets the injected SIMA document instance.
     */
    get simaDoc() {
        return this._simaDoc;
    }
    get surveyPoints() {
        return Array.from(this._surveyPoints.values());
    }
    get routeByGroup() {
        return Array.from(this._routeByGroup.values());
    }
    static fromSIMADoc(simaDoc, id) {
        const sima = new SIMA(simaDoc, id);
        const surveyPointsGroup = simaDoc.surveyPoints;
        surveyPointsGroup.forEach((surveyPoint) => {
            sima._surveyPoints.set(`${surveyPoint.point_number}-${surveyPoint.point_name}`, {
                position: new Cartesian3(surveyPoint.x, surveyPoint.y, surveyPoint.z),
                point_number: surveyPoint.point_number,
                point_name: surveyPoint.point_name,
            });
        });
        const sortedRoutes = [...simaDoc.simaRoutes].sort((a, b) => {
            const order = (type) => {
                if (type === SIMARouteType.IP_LINE)
                    return 1;
                if (type === SIMARouteType.MAIN_LINE)
                    return 2;
                if (type === SIMARouteType.CENTRAL_LINE)
                    return 3;
                return 4;
            };
            return order(a.routeType) - order(b.routeType);
        });
        sortedRoutes.forEach((route) => {
            let routeGroup = sima._routeByGroup.get(route.groupNumber);
            if (!routeGroup) {
                routeGroup = {
                    group_number: route.groupNumber,
                    ipLine: undefined,
                    mainLine: undefined,
                    centerLine: undefined,
                };
                sima._routeByGroup.set(route.groupNumber, routeGroup);
            }
            switch (route.routeType) {
                case SIMARouteType.IP_LINE:
                    routeGroup.ipLine = new IPLine(route, sima._surveyPoints);
                    break;
                case SIMARouteType.MAIN_LINE:
                    routeGroup.mainLine = new MainLine(route, sima._surveyPoints);
                    break;
                case SIMARouteType.CENTRAL_LINE:
                    routeGroup.centerLine = new CenterLine(route, sima._surveyPoints, routeGroup.mainLine);
                    break;
                default:
                    break;
            }
        });
        return sima;
    }
    hasCentralLine() {
        return Array.from(this._routeByGroup.values()).some((group) => group.centerLine !== undefined &&
            (group.centerLine.points?.length ?? 0) > 0);
    }
    export() {
        return this._simaDoc.export();
    }
    get pointsToCreateCrossSection() {
        return this._crossSectionPointsByGroup().flat();
    }
    /**
     * Direction at a cross-section point, blended from its incoming and outgoing segments.
     * Neighbors are taken within the same route group; zero-length segments are skipped.
     * @returns undefined when the point is not found or has no neighbor
     */
    getDirectionAtCenterlinePoint(pointId) {
        this._centerlineDirections ??= this._computeCenterlineDirections();
        return this._centerlineDirections.get(pointId);
    }
    /** Cross-section points per route group: the centerline, or the IP line when there is none. */
    _crossSectionPointsByGroup() {
        const groups = [];
        this._routeByGroup.forEach((routeGroup) => {
            const route = routeGroup.centerLine && (routeGroup.centerLine.points?.length ?? 0) > 0
                ? routeGroup.centerLine
                : routeGroup.ipLine;
            const points = [];
            if (route?.points) {
                for (let i = 0; i < route.points.length; i++) {
                    const point = route.points[i];
                    if (!point || !point.position)
                        continue;
                    const id = point.point_number !== undefined && point.point_name !== undefined
                        ? `${point.point_number}-${point.point_name}`
                        : `${routeGroup.group_number}-${i}`;
                    points.push({
                        id,
                        point: point.position,
                        pointType: PointType.INTERMEDIATE_POINT,
                        label: point.point_name ||
                            (point.point_number !== undefined
                                ? String(point.point_number)
                                : id),
                    });
                }
            }
            groups.push(points);
        });
        return groups;
    }
    /** Directions of all cross-section points keyed by id; a point shared by two groups blends both. */
    _computeCenterlineDirections() {
        const upDir = Cartesian3.UNIT_Z;
        const directions = new Map();
        const isDistinct = (a, b) => Cartesian2.distance(a, b) > MIN_SEGMENT_LENGTH;
        for (const points of this._crossSectionPointsByGroup()) {
            for (let i = 0; i < points.length; i++) {
                const current = points[i].point;
                let prevIndex = i - 1;
                while (prevIndex >= 0 && !isDistinct(points[prevIndex].point, current))
                    prevIndex--;
                let nextIndex = i + 1;
                while (nextIndex < points.length &&
                    !isDistinct(points[nextIndex].point, current))
                    nextIndex++;
                const prev = points[prevIndex];
                const next = points[nextIndex];
                const inDirection = prev
                    ? EarthBrainUtils.getDirectionOfPointOnSitePath(prev.point, current, upDir)
                    : undefined;
                const outDirection = next
                    ? EarthBrainUtils.getDirectionOfPointOnSitePath(current, next.point, upDir)
                    : undefined;
                const direction = inDirection && outDirection
                    ? EarthBrainUtils.addSiteDirection(inDirection, outDirection)
                    : (inDirection ?? outDirection);
                if (!direction)
                    continue;
                const existing = directions.get(points[i].id);
                directions.set(points[i].id, existing
                    ? EarthBrainUtils.addSiteDirection(existing, direction)
                    : direction);
            }
        }
        return directions;
    }
}
//# sourceMappingURL=sima.js.map